import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Calendar, MapPin, ChevronLeft, ChevronRight, Music, Youtube,
  Clock, Check, Expand, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useCountdown } from "../hooks/useCountdown";
import { whatsappUrl } from "../config";

const normalizePhone = (p) => p.replace(/\D/g, "");

const Section = ({ children, className = "" }) => (
  <div className={`px-6 py-10 text-center ${className}`}>{children}</div>
);

const Label = ({ children }) => (
  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-indigo-600">{children}</p>
);

const Countdown = ({ dateISO }) => {
  const t = useCountdown(dateISO);
  return (
    <div data-testid="demo-countdown" className="mt-5 flex justify-center gap-2">
      {[
        [t.d, "días"],
        [t.h, "hs"],
        [t.m, "min"],
        [t.s, "seg"],
      ].map(([v, l]) => (
        <div key={l} className="w-16 rounded-2xl border border-indigo-100 bg-indigo-50/70 py-3">
          <p className="text-2xl font-semibold text-indigo-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {String(v).padStart(2, "0")}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p>
        </div>
      ))}
    </div>
  );
};

const Gallery = ({ demo }) => {
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(false);
  const next = () => setIndex((index + 1) % demo.gallery.length);
  const prev = () => setIndex((index - 1 + demo.gallery.length) % demo.gallery.length);

  return (
    <div data-testid="demo-gallery">
      <div className="relative mx-auto aspect-[4/3] max-w-sm overflow-hidden rounded-2xl border border-slate-200">
        <AnimatePresence mode="wait">
          <motion.img
            key={index}
            src={demo.gallery[index]}
            alt={`Galería ${index + 1} de ${demo.gallery.length}`}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
            className="h-full w-full object-cover"
          />
        </AnimatePresence>
        <button
          data-testid="demo-gallery-zoom"
          onClick={() => setZoom(true)}
          aria-label="Ampliar imagen"
          className="absolute right-3 top-3 rounded-full bg-slate-900/50 p-2 text-white backdrop-blur-sm transition-colors hover:bg-slate-900/70"
        >
          <Expand className="h-4 w-4" />
        </button>
        <button
          data-testid="demo-gallery-prev"
          onClick={prev}
          aria-label="Imagen anterior"
          className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-slate-700 transition-colors hover:bg-white"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          data-testid="demo-gallery-next"
          onClick={next}
          aria-label="Imagen siguiente"
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-slate-700 transition-colors hover:bg-white"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 flex justify-center gap-1.5">
        {demo.gallery.map((_, i) => (
          <button
            key={i}
            data-testid={`demo-gallery-dot-${i}`}
            onClick={() => setIndex(i)}
            aria-label={`Ir a imagen ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-[#4F5BD5]" : "w-1.5 bg-slate-300"}`}
          />
        ))}
      </div>

      <AnimatePresence>
        {zoom && (
          <motion.div
            data-testid="demo-gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/90 p-4"
            onClick={() => setZoom(false)}
          >
            <img src={demo.gallery[index]} alt="Imagen ampliada" className="max-h-[85vh] max-w-full rounded-xl object-contain" />
            <button data-testid="demo-lightbox-close" aria-label="Cerrar" className="absolute right-4 top-4 text-white">
              <X className="h-7 w-7" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MusicPlayer = ({ demo }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <button
      data-testid="example-music-toggle"
      onClick={() => {
        setPlaying(!playing);
        toast(playing ? "Música pausada (demo)" : "Reproduciendo música (demo)", { duration: 1800 });
      }}
      className={`mx-auto flex w-full max-w-sm items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors duration-300 ${
        playing ? "border-indigo-300 bg-indigo-50" : "border-slate-200 bg-white hover:border-indigo-200"
      }`}
    >
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${playing ? "bg-[#4F5BD5] text-white" : "bg-indigo-50 text-indigo-600"}`}>
        <Music className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-slate-800">{demo.musicLabel}</span>
        <span className="block text-xs text-slate-400">{playing ? "Sonando…" : "Tocar para escuchar"}</span>
      </span>
      <span className="flex h-5 items-end gap-[3px]" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <motion.span
            key={i}
            className={`w-[3px] rounded-full ${playing ? "bg-[#4F5BD5]" : "bg-slate-300"}`}
            animate={playing ? { height: [6, 18, 9, 16, 6] } : { height: 6 }}
            transition={playing ? { repeat: Infinity, duration: 1.1, delay: i * 0.15 } : {}}
          />
        ))}
      </span>
    </button>
  );
};

const Rsvp = ({ demo }) => {
  const [phone, setPhone] = useState("");
  const [guest, setGuest] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [answered, setAnswered] = useState(null);

  const validate = () => {
    const found = demo.guests.find((g) => normalizePhone(g.phone) === normalizePhone(phone));
    if (found) {
      setGuest(found);
      setNotFound(false);
    } else {
      setGuest(null);
      setNotFound(true);
    }
  };

  if (demo.mode === "publica") {
    return (
      <div data-testid="demo-rsvp-public" className="mx-auto max-w-sm">
        <p className="text-sm text-slate-600">
          Esta invitación es de acceso público: cualquier persona con el enlace puede confirmar.
        </p>
        <button
          data-testid="example-rsvp-button"
          onClick={() => toast.success("Asistencia confirmada (demostración)")}
          className="mt-4 w-full rounded-full bg-[#4F5BD5] py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#3B46BC]"
        >
          Confirmar asistencia
        </button>
      </div>
    );
  }

  return (
    <div data-testid="demo-rsvp-private" className="mx-auto max-w-sm">
      <p className="text-sm text-slate-600">
        {demo.mode === "privada"
          ? "Esta invitación es privada: ingresá tu teléfono para validar que estás en la lista."
          : "La información general es pública, pero la confirmación y los pases requieren validar tu teléfono."}
      </p>
      <p className="mt-2 text-xs text-slate-400">
        Para probar, usá <span className="font-semibold text-slate-600">{demo.demoPhone}</span> (dato ficticio).
      </p>

      {!guest && (
        <div className="mt-4 flex gap-2">
          <input
            data-testid="demo-phone-input"
            type="tel"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setNotFound(false);
            }}
            onKeyDown={(e) => e.key === "Enter" && validate()}
            placeholder="Tu número de teléfono"
            className="min-w-0 flex-1 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition-colors focus:border-[#4F5BD5]"
          />
          <button
            data-testid="demo-phone-validate-button"
            onClick={validate}
            className="shrink-0 rounded-full bg-[#4F5BD5] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#3B46BC]"
          >
            Validar
          </button>
        </div>
      )}

      {notFound && (
        <p data-testid="demo-phone-not-found" className="mt-3 text-sm text-rose-600">
          Este número no figura en la lista del evento (demo).
        </p>
      )}

      <AnimatePresence>
        {guest && (
          <motion.div
            data-testid="demo-guest-card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-5 rounded-2xl border border-indigo-200 bg-indigo-50/60 p-5 text-left"
          >
            <p className="text-sm font-semibold text-slate-900">{guest.name}</p>
            <div className="mt-3 flex gap-2">
              <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200">
                {guest.passes} {guest.passes === 1 ? "pase" : "pases"}
              </span>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200">
                {guest.mesa}
              </span>
            </div>
            {!answered ? (
              <div className="mt-4 flex gap-2">
                <button
                  data-testid="demo-rsvp-confirm"
                  onClick={() => {
                    setAnswered("si");
                    toast.success("Asistencia confirmada (demostración)");
                  }}
                  className="flex-1 rounded-full bg-[#4F5BD5] py-2.5 text-xs font-semibold text-white transition-colors hover:bg-[#3B46BC]"
                >
                  Confirmar asistencia
                </button>
                <button
                  data-testid="demo-rsvp-decline"
                  onClick={() => {
                    setAnswered("no");
                    toast("Respuesta registrada (demostración)");
                  }}
                  className="flex-1 rounded-full border border-slate-300 bg-white py-2.5 text-xs font-semibold text-slate-600 transition-colors hover:border-slate-400"
                >
                  No podré asistir
                </button>
              </div>
            ) : (
              <p data-testid="demo-rsvp-done" className="mt-4 flex items-center gap-2 text-sm font-medium text-emerald-700">
                <Check className="h-4 w-4" />
                {answered === "si" ? "¡Nos vemos en el evento!" : "Gracias por avisar."}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const InvitationDemo = ({ demoKey, onClose, onSwitch }) => {
  const demo = demoKey ? demoKey : null;

  useEffect(() => {
    document.body.style.overflow = demo ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [demo]);

  return (
    <AnimatePresence>
      {demo && (
        <motion.div
          data-testid="invitation-demo-overlay"
          data-lenis-prevent
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-slate-950/60 backdrop-blur-sm"
        >
          <div className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur-xl sm:px-6">
            <div className="flex items-center gap-2">
              <span data-testid="demo-badge" className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
                Demostración
              </span>
              <span className="hidden text-sm text-slate-500 sm:block">{demo.modeLabel}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="demo-switch-button"
                onClick={onSwitch}
                className="rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 transition-colors hover:border-indigo-200 hover:text-indigo-700"
              >
                Cambiar ejemplo
              </button>
              <button
                data-testid="demo-close-button"
                onClick={onClose}
                aria-label="Cerrar demo"
                className="rounded-full bg-slate-900 p-2.5 text-white transition-colors hover:bg-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <motion.div
            key={demo.key}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto my-6 w-full max-w-lg overflow-hidden bg-white shadow-[0_24px_80px_-24px_rgba(15,23,42,0.4)] sm:rounded-3xl"
          >
            <div className="relative h-[420px]">
              <img src={demo.cover} alt={`Portada de ${demo.title}`} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-900/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-center text-white">
                <p className="text-[11px] uppercase tracking-[0.35em] text-white/80">{demo.subtitle}</p>
                <h3 className="mt-2 text-4xl font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {demo.title}
                </h3>
                <p className="mt-3 text-sm text-white/85">{demo.dateLabel}</p>
              </div>
            </div>

            <Section>
              <Label>Falta</Label>
              <Countdown dateISO={demo.dateISO} />
              <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
                <a
                  data-testid="demo-calendar-link"
                  href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(demo.title)}&dates=${new Date(demo.dateISO).toISOString().replace(/[-:]|\.\d{3}/g, "").slice(0, 15)}Z`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-xs font-semibold text-slate-700 transition-colors hover:border-indigo-200 hover:text-indigo-700"
                >
                  <Calendar className="h-4 w-4" />
                  Guardar en mi calendario
                </a>
                <a
                  data-testid="demo-map-link"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(demo.mapsQuery)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-xs font-semibold text-slate-700 transition-colors hover:border-indigo-200 hover:text-indigo-700"
                >
                  <MapPin className="h-4 w-4" />
                  {demo.locationName}
                </a>
              </div>
            </Section>

            <div className="border-t border-slate-100" />
            <Section>
              <Label>Galería</Label>
              <div className="mt-6">
                <Gallery demo={demo} />
              </div>
            </Section>

            <div className="border-t border-slate-100" />
            <Section>
              <Label>Música</Label>
              <div className="mt-6">
                <MusicPlayer demo={demo} />
              </div>
              <button
                data-testid="demo-video-placeholder"
                onClick={() => toast("Video de YouTube de demostración")}
                className="mx-auto mt-4 flex w-full max-w-sm items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left transition-colors hover:border-indigo-200"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                  <Youtube className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-800">{demo.videoTitle}</span>
                  <span className="block text-xs text-slate-400">Tocar para reproducir (demo)</span>
                </span>
              </button>
            </Section>

            <div className="border-t border-slate-100" />
            <Section>
              <Label>Itinerario</Label>
              <div className="mx-auto mt-6 max-w-sm space-y-0 text-left">
                {demo.itinerary.map((it, i) => (
                  <div key={it.time} className="relative flex gap-4 pb-6 last:pb-0">
                    {i < demo.itinerary.length - 1 && (
                      <span className="absolute left-[27px] top-8 h-full w-px bg-indigo-100" />
                    )}
                    <span className="flex w-14 shrink-0 items-start gap-1 text-sm font-semibold text-indigo-600" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      <Clock className="mt-0.5 h-3.5 w-3.5" />
                      {it.time}
                    </span>
                    <span className="text-sm text-slate-700">{it.title}</span>
                  </div>
                ))}
              </div>
            </Section>

            <div className="border-t border-slate-100" />
            <Section className="bg-gradient-to-b from-indigo-50/50 to-white">
              <Label>Confirmá tu asistencia</Label>
              <div className="mt-6">
                <Rsvp key={demo.key} demo={demo} />
              </div>
            </Section>

            <div className="border-t border-slate-100 px-6 py-6 text-center">
              <p className="text-xs text-slate-400">
                Invitación de demostración creada con Invitaciones Digitales Plus · Pro360
              </p>
              <a
                data-testid="demo-whatsapp-footer"
                href={whatsappUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                Quiero una invitación así
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
